import React, {
  useRef,
  useEffect,
  useState,
  forwardRef,
  useContext,
} from 'react';
import styled from 'astroturf/react';

import { Button } from 'js/design_system/Button';
import { TextButton } from 'js/design_system/TextButton';
import { Colors } from 'js/design_system/Color';
import CloseIcon from 'js/design_system/icons/close.svg';
import Send from './icons/send.svg';
import SuggestionsForm from './SuggestionsForm';
import { Spinner } from 'js/academia_components/transients/spinner';
import { NavigationContext, ReportContext } from './types';
import SuggestedQueryButton from './SuggestedQueryButton';

export interface Props {
  text: string;
  setText(text: string): void;
  onTextSubmitted(text: string): void;
  inputDisabled: boolean;
  suggestions?: string[] | null;
  submitCtaText?: string;
}

export default function InputForm({
  text,
  setText,
  onTextSubmitted,
  inputDisabled,
  suggestions,
  submitCtaText,
}: Props) {
  const containerRef = useRef<HTMLFormElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const hasSuggestions = !!suggestions && suggestions.length > 0;
  const [hasFreshSuggestions, setHasFreshSuggestions] = useState(false);
  useEffect(() => {
    setHasFreshSuggestions(!!suggestions && suggestions.length > 0);
  }, [suggestions]);

  const { report, hasScrolledPastRelatedQueries } = useContext(ReportContext);
  const relatedQueries = report?.relatedQueries;
  const hasRelatedQueries = !!relatedQueries && relatedQueries.length > 0;

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setText(e.target.value);
    // If the user has modified their input since receiving suggestions,
    // they should be able to submit that instead.
    setHasFreshSuggestions(false);
  };

  const submitDisabled = inputDisabled || hasFreshSuggestions;

  // Focus the textarea to make it clear that input is required
  const focusTextarea = () => {
    textareaRef.current?.focus();
  };

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();

    if (text.trim() === '') {
      focusTextarea();
      return;
    }

    if (!submitDisabled) {
      onTextSubmitted(text);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const { reportLayout } = useContext(NavigationContext);

  const relatedQuestion =
    !hasSuggestions && hasRelatedQueries && hasScrolledPastRelatedQueries ? (
      <SuggestedQueryContainer>
        <span>Related:</span>
        <SuggestedQueryButton
          compact
          showIcon={false}
          query={relatedQueries[0]}
          referringReportId={report.id}
        />
      </SuggestedQueryContainer>
    ) : null;

  return (
    <StickyFormBody
      ref={containerRef}
      onSubmit={handleSubmit}
      atBottom={reportLayout === 'top_down'}
    >
      {reportLayout === 'top_down' && relatedQuestion}

      <InputBackground>
        <ExpandingTextArea
          ref={textareaRef}
          placeholder="Ask a question to find answers from the literature."
          value={text}
          inputMode="search"
          // @ts-ignore - this is the only attribute safari seems to know about
          enterkeyhint="search"
          disabled={inputDisabled}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
        />
        {text && (
          <ClearButton
            icon={<CloseIcon color={Colors.GrayDark} />}
            onClick={() => setText('')}
          />
        )}
      </InputBackground>

      <SubmitButton
        icon={
          submitCtaText ? undefined : inputDisabled ? (
            <Spinner show />
          ) : (
            <Send />
          )
        }
        onClick={handleSubmit}
        disabled={submitDisabled}
      >
        {submitCtaText ? submitCtaText : undefined}
      </SubmitButton>

      <SuggestionsForm items={suggestions} />

      {reportLayout === 'normal' && relatedQuestion}
    </StickyFormBody>
  );
}

const StickyFormBody = forwardRef(
  (props: React.ComponentPropsWithRef<'form'> & { atBottom: boolean }) => {
    const [el, setEl] = useState<HTMLFormElement | null>(null);
    const [isSticky, setIsSticky] = useState(false);

    useEffect(() => {
      if (el) {
        const observer = new IntersectionObserver(
          (entries) => {
            setIsSticky(!entries[0].isIntersecting);
          },
          {
            rootMargin: '-1px 0px -1px 0px',
            threshold: 1.0,
          },
        );

        observer.observe(el);

        return () => observer.disconnect();
      }
    }, [el, props.atBottom]);

    return <FormBody ref={(el) => setEl(el)} isSticky={isSticky} {...props} />;
  },
);

export const ExpandingTextArea = React.forwardRef<
  HTMLTextAreaElement,
  React.TextareaHTMLAttributes<HTMLTextAreaElement> & { value: string }
>((props, ref) => {
  const [textarea, setTextarea] = useState<HTMLTextAreaElement | null>(null);
  const { placeholder, value, ...rest } = props;

  useEffect(() => {
    // Hack; force a rerender with `setTimeout`.
    const timer = setTimeout(() => {
      if (textarea) {
        textarea.style.height = 'auto';
        textarea.style.height = `${textarea.scrollHeight}px`;
      }
    }, 0);

    return () => clearTimeout(timer);
  }, [textarea, value, props.defaultValue]);

  return (
    <BorderlessTextArea
      {...rest}
      placeholder={placeholder}
      value={value}
      ref={(el) => {
        setTextarea(el ?? null);
        if (typeof ref === 'function') {
          ref(el);
        } else if (ref) {
          ref.current = el;
        }
      }}
      // without this it's defaulting to 2 rows
      rows={props.rows || 1}
    />
  );
});

const FormBody = styled.form<{
  isSticky?: boolean;
  atBottom?: boolean;
}>`
  font-size: 16px;
  background: white;
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px 8px;

  & > button {
    display: none;
  }

  @include for-tablet-sm-up {
    font-size: 20px;
    grid-template-columns: 1fr auto;

    & > button {
      display: initial;
    }
  }

  &:not(&.atBottom) {
    position: sticky;
    top: 0;
    // Add spacing when sticky, without disturbing siblings when non-sticky
    padding: 12px 16px;
    margin: -12px -16px;
    border: 1px solid transparent;
    transition:
      background 200ms,
      box-shadow 200ms;

    &.isSticky {
      z-index: 1;

      background: #f9f7f4;
      border-radius: 0 0 4px 4px;
      border: 1px solid #e0d9c6;
      border-top: none;
      box-shadow: 0px 2px 4px rgba(34, 34, 51, 0.25);

      // Transition the input's background
      & > div {
        background: white;
      }
    }
  }
`;

const BorderlessTextArea = styled.textarea`
  font-size: inherit;

  display: block;
  font-family: Roboto;
  background: none;
  width: 100%;
  height: auto;
  padding: 16px;
  margin: 0;

  border: none;
  outline: none;
  box-shadow: none;
  resize: none;
  overflow: hidden;
  word-wrap: break-word;
  white-space: pre-wrap;
  appearance: none;
  color: black;

  line-height: 32px;

  &::placeholder {
    color: #535366;
    opacity: 1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const InputBackground = styled.div`
  display: flex;
  border-radius: 8px;
  border: 1px solid #e0d9c6;
  background: #f9f7f4;
  align-items: flex-start;
  flex-grow: 1;

  transition:
    background 200ms,
    box-shadow 200ms,
    border-color 200ms;

  &:focus-within {
    border-color: #0c3b8d;
  }
`;

const ClearButton = styled(TextButton)`
  &:not(&--inline) {
    height: 64px;
    padding: 16px;
  }

  &:hover {
    svg {
      fill: $ds-gray-dark;
    }
  }

  svg {
    fill: #91919e;
    width: 24px;
    height: 24px;
  }

  span {
    display: none;
  }
`;

const SubmitButton = styled(Button)`
  min-height: 66px;
  box-shadow: none;

  svg {
    fill: currentColor;
    height: 24px;
    margin: 0 !important; // overrides an inline style
  }
`;

const SuggestedQueryContainer = styled.span`
  grid-column: 1/3;

  font-size: 14px;
  align-items: baseline;
  gap: 0.5em;

  > span {
    color: #535366;
  }

  display: none;
  @include for-tablet-sm-up {
    display: inline-flex;
  }
`;
